import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { from, Observable, of, throwError, timer } from 'rxjs';
import { switchMap, shareReplay, retry, catchError, tap } from 'rxjs/operators';

import { HttpConfig } from '../models/http-config.model';
import { SessionStorage } from './session-storage.service';
import { configureApiShared, getApiKey, getSharedDirectories, invalidateApiKey } from './api-shared';
import { format } from 'date-fns';

@Injectable({
    providedIn: 'root'
})
export class HttpService {
    public baseURL = 'https://api.rezbot.com/api/v1.1/';
    public publicKey: string;
    public appKey = 'geronigobookit';
    public apiKey = 'rezbotbookit';
    public mode: 'live'|'test' = 'live';
    public params = { origin_app: '', origin_cta: '' };
    public extraParams = { origin_app: '', origin_cta: '' };
    public lang = 'en';
    public view_id: string;
    public furthest_step: string;
    public view_params: any = {};
    public steps = ['customer-details', 'enquiry','paymentform', 'transaction', 'success','fields','fields-success'];
    public xWidget = 'wc-bookit';

    private publicKeyCache$: Observable<string> | null = null;
    public shared_storage = true;

    constructor(
        private http: HttpClient,
        private sessionStorage: SessionStorage
    ) { }

    public get isTestMode(): boolean {
        return this.mode === 'test';
    }

    public configureSharedApi(): void {
        configureApiShared({ baseURL: this.baseURL, appKey: this.appKey, apiKey: this.apiKey, xWidget: this.xWidget });
    }

    public get isDev(): boolean {
        return this.baseURL.indexOf('apidev') >=0;
    }

    public setApi(version: string): void {
        const versions = [
            {
                name: 'live',
                url: 'https://api.rezbot.com/api/v1.1/'
            },
            {
                name: 'stage',
                url: 'https://apistage.rezbot.com/api/v1.1/'
            },
            {
                name: 'dev1',
                url: 'https://apidev.rezbot.com/api/v1.1/'
            },
            {
                name: 'dev2',
                url: 'https://apidev2.rezbot.com/api/v1.1/'
            },
            {
                name: 'local',
                url: 'https://geronigo.djanes/api/v1.1/'
            }
        ];

        const url = window.location.href;
        const defaultVersion = url.includes('gitlab') || url.includes('localhost') ? 'stage' : 'live';

        let api: { name: string, url: string };

        if (version) api = versions.find(item => item.name === version);
        if (!version || !api) api = versions.find(item => item.name === defaultVersion);

        this.baseURL = api.url;
    }

    public getPublicKey(): Observable<string> {
        if (this.shared_storage && this.appKey && this.apiKey) {
            this.configureSharedApi();
            return from(getApiKey()).pipe(tap((token: string) => {
                this.publicKey = token;
                this.storeDirectories(getSharedDirectories());
            }));
        }

        if (this.publicKey) return of(this.publicKey);

        const storedKey = this.getKeyFromSessionStorage();
        if (storedKey) {
            this.publicKey = storedKey;
            return of(storedKey);
        }

        if (this.publicKeyCache$) return this.publicKeyCache$;

        this.publicKeyCache$ = this.http.get<any>(`${this.baseURL}/auths?access=public`, {
            observe: 'response',
            responseType: 'text' as 'json',
            headers: {
                'X-API-KEY': this.apiKey,
                'X-APP-KEY': this.appKey,
                'X-WIDGET': this.xWidget
            }
        }).pipe(
            switchMap(response => this.cleanResponse(response)),
            switchMap(data => {
                const token = data.body?.auths?.jwt;
                this.publicKey = token;
                this.sessionStorage.setItem(this.getPublicKeyStorageKey(), token);
                this.storeDirectories(data.body?.directories);
                this.publicKeyCache$ = null;
                return of(token);
            }),
            catchError(err => {
                this.publicKeyCache$ = null;
                return throwError(() => err);
            }),
            shareReplay({ bufferSize: 1, refCount: false })
        );

        return this.publicKeyCache$;
    }

    public get(url: string, config?: HttpConfig): Observable<HttpResponse<any>> {
        return this.makeAuthenticatedRequest('GET', url, config);
    }

    public post(url: string, body: any, config?: HttpConfig): Observable<HttpResponse<any>> {
        return this.makeAuthenticatedRequest('POST', url, config, body);
    }

    public patch(url: string, body: object, config?: HttpConfig): Observable<HttpResponse<any>> {
        return this.makeAuthenticatedRequest('PATCH', url, config, body);
    }

    public put(url: string, body: object, config?: HttpConfig): Observable<HttpResponse<any>> {
        return this.makeAuthenticatedRequest('PUT', url, config, body);
    }

    public delete(url: string, config?: HttpConfig): Observable<HttpResponse<any>> {
        return this.makeAuthenticatedRequest('DELETE', url, config);
    }

    public getFile(url: string, config?: HttpConfig): Observable<HttpResponse<Blob>> {
        return this.http.get(`${this.baseURL}${url}`, {
            responseType: 'blob', observe: 'response', ...this.parseConfig(config)
        }).pipe(retry({ count: 2, delay: (err, index) => {
            if (index > 2 || (err.status !== 401 && err.status !== 403)) return throwError(() => err);
            this.invalidatePublicKey();
            return timer(2000 * index);
        }}));
    }

    public getMissingDirectories(requiredDirectories: string[]): string[] {
        this.sessionStorage.setItem('requiredDirectories', JSON.stringify(requiredDirectories));
        const directories = this.getStoredDirectories();
        return requiredDirectories.filter(directory => !this.hasDirectory(directories, directory));
    }

    public getStoredDirectories(): any {
        const directories = this.sessionStorage.getItem('directories');
        if (!directories) return {};
        try { return JSON.parse(directories) || {}; } catch (err) { return {}; }
    }

    public storeDirectories(directories: any): any {
        if (!directories) return this.getStoredDirectories();
        const mergedDirectories = { ...this.getStoredDirectories(), ...this.normaliseDirectories(directories) };
        this.sessionStorage.setItem('directories', JSON.stringify(mergedDirectories));
        return mergedDirectories;
    }

    public invalidatePublicKey(): void {
        if (this.shared_storage) invalidateApiKey();
        this.sessionStorage.removeItem(this.getPublicKeyStorageKey());
        this.publicKey = undefined;
        this.publicKeyCache$ = null;
    }

    private makeAuthenticatedRequest(method: string, url: string, config?: HttpConfig, body?: any): Observable<HttpResponse<any>> {
        return of(null).pipe(
            switchMap(() => this.getRequestApiKey(config)),
            switchMap(publicKey => {
                const authenticatedConfig = this.parseConfigWithAuth(config, publicKey);
                const options: any = { observe: 'response', responseType: 'text' as 'json', ...authenticatedConfig };
                let request: Observable<HttpResponse<any>>;
                switch (method) {
                    case 'GET': request = this.http.get<any>(`${this.baseURL}${url}`, options) as Observable<HttpResponse<any>>; break;
                    case 'POST': request = this.http.post<any>(`${this.baseURL}${url}`, body, options) as Observable<HttpResponse<any>>; break;
                    case 'PUT': request = this.http.put<any>(`${this.baseURL}${url}`, body, options) as Observable<HttpResponse<any>>; break;
                    case 'PATCH': request = this.http.patch<any>(`${this.baseURL}${url}`, body, options) as Observable<HttpResponse<any>>; break;
                    case 'DELETE': request = this.http.delete<any>(`${this.baseURL}${url}`, options) as Observable<HttpResponse<any>>; break;
                    default: return throwError(() => new Error(`Unsupported HTTP method: ${method}`));
                }
                return request.pipe(
                    switchMap(response => this.cleanResponse(response)),
                    catchError(err => {
                        if (err.status === 401 || err.status === 403) this.invalidatePublicKey();
                        return throwError(() => err);
                    })
                );
            }),
            retry({ count: 2, delay: (err, index) => {
                if (index > 2 || (err.status !== 401 && err.status !== 403)) return throwError(() => err);
                return timer(2000 * index);
            }})
        );
    }

    private getRequestApiKey(config?: HttpConfig): Observable<string> {
        const explicitKey = config?.headers?.['X-API-KEY'];
        return explicitKey ? of(explicitKey) : this.getPublicKey();
    }

    private parseConfigWithAuth(config?: HttpConfig, publicKey?: string): HttpConfig {
        const newConfig: HttpConfig = { headers: { 'X-APP-KEY': this.appKey, 'X-WIDGET': this.xWidget } };
        if (publicKey) newConfig.headers['X-API-KEY'] = publicKey;
        newConfig.params = this.filterParams({ ...(config?.params || {}), ...this.params });
        if (config?.headers) newConfig.headers = { ...newConfig.headers, ...this.filterParams(config.headers) };
        if (config?.body) newConfig.body = this.filterParams(config.body);
        return newConfig;
    }

    private parseConfig(config?: HttpConfig): HttpConfig {
        const newConfig: HttpConfig = { headers: { 'X-APP-KEY': this.appKey, 'X-WIDGET': this.xWidget } };
        newConfig.params = this.filterParams({ ...(config?.params || {}), ...this.params });
        if (config?.headers) newConfig.headers = { ...newConfig.headers, ...this.filterParams(config.headers) };
        if (config?.body) newConfig.body = this.filterParams(config.body);
        return newConfig;
    }

    private filterParams(params: { [key: string]: any }): any {
        const data: any = {};

        Object.keys(params).forEach(key => {
            if (!params.hasOwnProperty(key) || !params[key]) return;

            if (params[key] === true || params[key] === false) data[key] = params[key] ? '1' : '0';
            //else if (params[key] instanceof Date) data[key] = moment(params[key], 'YYYYMMDD');
            else if (params[key] instanceof Date) data[key] = format(params[key], 'yyyyMMdd');
            else data[key] = params[key];
        });

        return data;
    }

    private getKeyFromSessionStorage(): string {
        const key = this.sessionStorage.getItem(this.getPublicKeyStorageKey());

        try {
            JSON.parse(key);

            return null;
        } catch (err) {
            return key;
        }
    }

    private getPublicKeyStorageKey(): string {
        return ['publicKey', this.appKey || '', this.xWidget || ''].join(':');
    }

    private hasDirectory(directories: any, directory: string): boolean {
        return Object.prototype.hasOwnProperty.call(directories || {}, directory) && directories[directory] !== null && directories[directory] !== undefined;
    }

    private normaliseDirectories(directories: any): any {
        if (!directories || typeof directories !== 'object') return {};
        if (!Array.isArray(directories)) return directories;
        return directories.reduce((items, directory) => {
            if (!directory || typeof directory !== 'object') return items;
            const name = directory.directory || directory.name || directory.key || directory.type;
            const value = directory.response ?? directory.data ?? directory.body ?? directory.items ?? directory.value;
            if (name && value !== undefined) { items[name] = value; return items; }
            return { ...items, ...directory };
        }, {});
    }

    private cleanResponse(response: HttpResponse<any>): Observable<HttpResponse<any>> {
        if (!(response.headers.get('Content-Type') || '').includes('application/json')) return of(response);
        try {
            const parsedBody = JSON.parse(this.cleanPhpErrors(response.body));
            return of(new HttpResponse({ body: parsedBody, headers: response.headers, status: response.status, statusText: response.statusText, url: response.url }));
        } catch (error) {
            return throwError(() => new Error('Invalid Response'));
        }
    }

    private cleanPhpErrors(responseText: string): string {
        if (!responseText) return responseText;
        const match = responseText.match(/^[\s\S]*?([\{\[])/);
        return match ? responseText.substring(responseText.indexOf(match[1])) : responseText;
    }

    public enocodeJSON(element: any, key?: string, list?: any[]): string {
        list = list || [];

        if (typeof (element) === 'object') {
            for (const idx in element) this.enocodeJSON(element[idx], key ? key + '[' + idx + ']' : idx, list);
        } else {
            list.push(key + '=' + encodeURIComponent(element));
        }

        return list.join('&');
    }

    public logWidgetView(step: string, params = {}): void {
        console.log('step', step);
        const method = this.view_id ? 'put' : 'post';
        this.view_params = { ...(this.view_params || {}), ...(params || {}) };

        const adId = this.view_params.adId || this.view_params.ad_id || this.view_params.affiliate_id || this.view_params.affiliateId;
        const affiliateId = this.view_params.affiliate_id || this.view_params.affiliateId || this.view_params.adId || this.view_params.ad_id;
        const splitTest = this.view_params['splitTest'] || this.view_params['split_test'];

        if (adId) this.view_params.adId = adId;
        if (affiliateId) {
            this.view_params.affiliate_id = affiliateId;
            this.view_params.affiliateId = affiliateId;
        }
        if(splitTest){
            this.view_params['splitTest'] = splitTest;
            this.view_params['split_test'] = splitTest;
        }

        //if (this.view_params.total == 0) return;
        if (this.steps.indexOf(step) > this.steps.indexOf(this.furthest_step)) this.furthest_step = step;
        if (!this.view_params.steps) this.view_params.steps = [];
        if (this.view_params.steps.indexOf(step) !== -1) return;
        this.view_params.steps.push(step);

        //console.log(this.view_params);

        this[method]('/widget_views', {
            widget_view_id: this.view_id,
            affiliate_id: this.view_params.affiliate_id,
            company_id: this.view_params.company_id,
            widget: this.xWidget,
            ts: `${Math.floor(Date.now() / 1000)}`,
            domain: window.location.hostname,
            referrer: window.location.href,
            step,
            user_agent: navigator.userAgent,
            furthest_step: this.furthest_step,
            params: JSON.stringify(this.view_params),
            split_test: this.view_params.split_test
        }).subscribe((response: any) => {
            if(method == 'post' && response.body?.widget_views?.[0]?.widget_view_id){
                this.view_id = response.body.widget_views[0].widget_view_id;
            }
        });
    }
}
